/**
 * BoardMobileList — the mobile fallback for the board: no infinite canvas,
 * just viewing/editing/filtering/details, per the responsive spec. Cards
 * created here default to position {0,0}; they get a real canvas position
 * once opened on a larger screen.
 */

import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { Input } from '@/components/ui/input';
import { useStoryBoard } from './context/StoryBoardContext';
import { computeHighlightSet } from './utils/boardHelpers';
import { NODE_TYPES, NODE_TYPE_MAP, STATUS_OPTIONS } from './constants';
import AddNodeMenu from './AddNodeMenu';
import CharacterFilterBar from './CharacterFilterBar';

export default function BoardMobileList({ storyId, board, selectedCharacterIds, onToggleCharacter, onClearCharacters, onOpenNode }) {
  const { addNode } = useStoryBoard();
  const [typeFilter, setTypeFilter] = useState('all');
  const [query, setQuery] = useState('');

  const characters = board.nodes.filter((n) => n.type === 'character');
  const highlightSet = useMemo(
    () => computeHighlightSet(board.nodes, board.connections, selectedCharacterIds),
    [board.nodes, board.connections, selectedCharacterIds]
  );
  const muteActive = selectedCharacterIds.length > 0;

  const visibleNodes = board.nodes.filter((n) => {
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    if (query.trim() && !n.title.toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search cards…"
            className="rounded-xl border-line bg-cream pl-8"
          />
        </div>
        <AddNodeMenu onAdd={(type) => addNode(storyId, type, { x: 0, y: 0 })} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setTypeFilter('all')}
          className={cx(
            'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
            typeFilter === 'all' ? 'border-coral bg-coral/10 text-coral' : 'border-line text-subtle hover:border-coral/40 hover:text-ink'
          )}
        >
          All
        </button>
        {NODE_TYPES.map(({ type, label, emoji }) => (
          <button
            key={type}
            type="button"
            onClick={() => setTypeFilter(type)}
            className={cx(
              'flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              typeFilter === type ? 'border-coral bg-coral/10 text-coral' : 'border-line text-subtle hover:border-coral/40 hover:text-ink'
            )}
          >
            <span className="leading-none">{emoji}</span> {label}
          </button>
        ))}
      </div>

      <CharacterFilterBar
        characters={characters}
        selectedIds={selectedCharacterIds}
        onToggle={onToggleCharacter}
        onClear={onClearCharacters}
      />

      {visibleNodes.length === 0 ? (
        <p className="py-10 text-center text-sm text-subtle">No cards match yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {visibleNodes.map((node) => {
            const config = NODE_TYPE_MAP[node.type];
            const Icon = config?.icon;
            const statusLabel = STATUS_OPTIONS.find((s) => s.value === node.status)?.label;
            const muted = muteActive && !highlightSet.has(node.id);
            return (
              <button
                key={node.id}
                type="button"
                onClick={() => onOpenNode(node.id)}
                className={cx(
                  'flex items-center gap-3 rounded-xl border border-line bg-card p-3 text-left shadow-card transition-opacity',
                  muted && 'opacity-30 grayscale'
                )}
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-cream">
                  {Icon && <Icon className="h-4 w-4 text-subtle" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-sm font-semibold text-ink">{node.title}</span>
                  {node.description && <span className="block truncate text-xs text-subtle">{node.description}</span>}
                </span>
                {statusLabel && (
                  <span className="shrink-0 rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold text-subtle">
                    {statusLabel}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
